import React, { useState } from 'react'
import { Alert, AlertTitle, Collapse, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { useTranslation } from 'react-i18next'
import { useLicense } from '../../hooks/useLicense'

/**
 * Displays a dismissible warning banner when the license expires within 30 days.
 * Place it once near the top of the app shell (e.g. in main.tsx, above the page content).
 */
export default function ExpiryBanner() {
  const { t } = useTranslation()
  const { isExpiringSoon, daysUntilExpiry, validTo } = useLicense()
  const [dismissed, setDismissed] = useState(false)

  if (dismissed || !isExpiringSoon()) return null

  const days = daysUntilExpiry()
  const formattedDate = validTo
    ? new Date(validTo).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : ''

  return (
    <Collapse in={!dismissed}>
      <Alert
        icon={<WarningAmberOutlinedIcon fontSize="inherit" />}
        severity="warning"
        sx={{
          borderRadius: 0,
          borderBottom: '1px solid #f59e0b',
          '& .MuiAlert-message': { width: '100%' },
        }}
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={() => setDismissed(true)}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        }
      >
        <AlertTitle sx={{ fontWeight: 700 }}>
          {t('license.expiryBanner.title')}
        </AlertTitle>
        {days === 1
          ? t('license.expiryBanner.messageToday', { date: formattedDate })
          : t('license.expiryBanner.message', { days, date: formattedDate })}
      </Alert>
    </Collapse>
  )
}
